import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Grid, InputLabel } from "@mui/material";

/**
 * SecureFields - Reusable Eway Secure Fields component for React
 *
 * Props:
 *   apiKey: Eway public API key (required)
 *   onToken: function(token) - callback when token is received (required)
 *   className: optional CSS class for container
 *   style: optional style object for container
 */
const SecureFields = ({ apiKey, onToken, open, className, style }) => {
  // Unique IDs for each field
  const cardId = useRef(`secure-field-card-${Math.random().toString(36).substr(2, 9)}`);
  const expiryId = useRef(`secure-field-expiry-${Math.random().toString(36).substr(2, 9)}`);
  const cvnId = useRef(`secure-field-cvn-${Math.random().toString(36).substr(2, 9)}`);
  const nameId = useRef(`secure-field-name-${Math.random().toString(36).substr(2, 9)}`);

  // Detect mobile webview context
  const isWebView =
    typeof window !== "undefined" &&
    window.ReactNativeWebView &&
    typeof window.ReactNativeWebView.postMessage === "function";

  // Track validity of all fields
  const fieldsValidRef = useRef({ card: false, expiry: false, cvn: false, name: false });
  const latestSecureFieldCodeRef = useRef(null);
  // Expose isFieldsValid for parent to enable submit
  useEffect(() => {
    window.isSecureFieldsValid = () => {
      const valid = Object.values(fieldsValidRef.current).every(Boolean);
      return valid;
    };
  }, []);

  // Expose getToken method for parent to call on submit
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.getSecureFieldsToken = async () => {
        if (window.eWAY && typeof window.eWAY.getToken === "function") {
          // getToken returns a promise with the token after all fields are valid
          try {
            const token = await window.eWAY.getToken();
            if (onToken && token) {
              onToken(token);
            }
            return token;
          } catch (err) {
            return null;
          }
        } else {
          return null;
        }
      };
    }
  }, [onToken]);

  // Track previous open state to detect transition
  const prevOpenRef = useRef(false);
  useEffect(() => {
    // Copy ref values to local variables for cleanup
    const cardIdVal = cardId.current;
    const expiryIdVal = expiryId.current;
    const cvnIdVal = cvnId.current;
    const nameIdVal = nameId.current;

    if (open && !prevOpenRef.current && window.eWAY && typeof window.eWAY.setupSecureField === "function") {
      // Only inject fields when open transitions from false to true
      [cardIdVal, expiryIdVal, cvnIdVal, nameIdVal].forEach((id) => {
        const el = document.getElementById(id);
        if (el) {
          el.innerHTML = "";
        }
      });
      // Shared callback for all fields
      const secureFieldCallback = (data) => {
        fieldsValidRef.current[data.targetField] = data.valueIsValid;
        // Only update code if valid
        if (data.valueIsValid && data.secureFieldCode) {
          latestSecureFieldCodeRef.current = data.secureFieldCode;
        }
        // If all fields valid, call onToken with latest code
        if (Object.values(fieldsValidRef.current).every(Boolean) && latestSecureFieldCodeRef.current) {
          if (onToken) {
            onToken(latestSecureFieldCodeRef.current);
          }
        }
      };
      // Card Number
      window.eWAY.setupSecureField(
        {
          publicApiKey: apiKey,
          fieldDivId: cardIdVal,
          fieldType: "card",
          styles:
            "font-family:'Plus Jakarta Sans',Roboto,Arial,sans-serif;font-size:16px;color:#222;background-color:#EAEDF1;border:none;border-radius:8px;padding:12px;box-sizing:border-box;outline:none;box-shadow:none;height:34px;",
          placeholder: "",
        },
        secureFieldCallback
      );
      // Expiry
      window.eWAY.setupSecureField(
        {
          publicApiKey: apiKey,
          fieldDivId: expiryIdVal,
          fieldType: "expirytext",
          styles:
            "font-family:'Plus Jakarta Sans',Roboto,Arial,sans-serif;font-size:16px;color:#222;background-color:#EAEDF1;border:none;border-radius:8px;padding:12px;box-sizing:border-box;outline:none;box-shadow:none;height:34px;",
          placeholder: "",
        },
        secureFieldCallback
      );
      // CVN
      window.eWAY.setupSecureField(
        {
          publicApiKey: apiKey,
          fieldDivId: cvnIdVal,
          fieldType: "cvn",
          styles:
            "font-family:'Plus Jakarta Sans',Roboto,Arial,sans-serif;font-size:16px;color:#222;background-color:#EAEDF1;border:none;border-radius:8px;padding:12px;box-sizing:border-box;outline:none;box-shadow:none;height:34px;",
          placeholder: "",
        },
        secureFieldCallback
      );
      // Cardholder Name
      window.eWAY.setupSecureField(
        {
          publicApiKey: apiKey,
          fieldDivId: nameIdVal,
          fieldType: "name",
          styles:
            "font-family:'Plus Jakarta Sans',Roboto,Arial,sans-serif;font-size:16px;color:#222;background-color:#EAEDF1;border:none;border-radius:8px;padding:12px;box-sizing:border-box;outline:none;box-shadow:none;height:34px;",
          placeholder: "",
        },
        secureFieldCallback
      );
    }
    prevOpenRef.current = open;
    // Cleanup: clear field containers when dialog closes or component unmounts
    return () => {
      if (!open) {
        [cardIdVal, expiryIdVal, cvnIdVal, nameIdVal].forEach((id) => {
          const el = document.getElementById(id);
          if (el) {
            el.innerHTML = "";
          }
        });
      }
    };
  }, [apiKey, onToken, open]);

  return (
    <Box className={className} sx={{ ...style, width: "100%", margin: 0, padding: 0, mb: 0 }}>
      <Grid
        container
        spacing={0}
        columnSpacing={0}
        rowSpacing={0}
        sx={{ width: "100%", margin: 0, padding: 0, maxWidth: 400 }}
      >
        <Grid item xs={12} sx={{ pl: 0 }}>
          <InputLabel shrink htmlFor={cardId.current} sx={{ fontWeight: 500, mb: 0 }}>
            CARD NUMBER
          </InputLabel>
          <Box id={cardId.current} sx={{ height: 40, minHeight: 48, width: "100%", mb: 0 }} />
        </Grid>
        {/* Expiry and CVN: always side by side in webview, original layout for web */}
        {isWebView ? (
          <Grid container spacing={0} columnSpacing={0} rowSpacing={0} sx={{ width: "100%", margin: 0, padding: 0 }}>
            <Grid item xs={6} sx={{ pl: 0 }}>
              <InputLabel shrink htmlFor={expiryId.current} sx={{ fontWeight: 500, mb: 0 }}>
                EXPIRY (MM/YY)
              </InputLabel>
              <Box id={expiryId.current} sx={{ height: 40, minHeight: 48, width: "100%", mb: 0 }} />
            </Grid>
            <Grid item xs={6} sx={{ pl: 2 }}>
              <InputLabel shrink htmlFor={cvnId.current} sx={{ fontWeight: 500, mb: 0 }}>
                CVN
              </InputLabel>
              <Box id={cvnId.current} sx={{ height: 40, minHeight: 48, width: "100%", mb: 0 }} />
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6} sx={{ pl: 0 }}>
              <InputLabel shrink htmlFor={expiryId.current} sx={{ fontWeight: 500, mb: 0 }}>
                EXPIRY (MM/YY)
              </InputLabel>
              <Box id={expiryId.current} sx={{ height: 40, minHeight: 48, width: "100%", mb: 0 }} />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ pl: 2 }}>
              <InputLabel shrink htmlFor={cvnId.current} sx={{ fontWeight: 500, mb: 0 }}>
                CVN
              </InputLabel>
              <Box id={cvnId.current} sx={{ height: 40, minHeight: 48, width: "100%", mb: 0 }} />
            </Grid>
          </>
        )}
        <Grid item xs={12} sx={{ pl: 0 }}>
          <InputLabel shrink htmlFor={nameId.current} sx={{ fontWeight: 500, mb: 0 }}>
            NAME ON CARD
          </InputLabel>
          <Box id={nameId.current} sx={{ height: 40, minHeight: 48, mb: 0, width: "100%" }} />
        </Grid>
      </Grid>
    </Box>
  );
};

SecureFields.propTypes = {
  apiKey: PropTypes.string.isRequired,
  onToken: PropTypes.func.isRequired,
  open: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default SecureFields;
